import type { JournalType } from './safeJournalApi';
import { DOC_TYPES, type DocType } from './docs/types';

/**
 * 일지 양식 정의 (DOC-122) — 편집 창 오른쪽 A4 미리보기가 이 값을 읽어 그린다.
 *
 * <p>칸은 작성 창과 같다 — 작성 창은 기존 사이트 화면(「보미라운지 안전관리 탭.pptx」 · DOC-122 §10)을 따른다.
 * 작성 창에서 뺀 칸(제목 · 참석자 · 특이사항 · 지적사항 · 날씨)은 여기에도 없다.
 *
 * <p>기본값은 서버 PDF 틀({@code SafeJournalDocService})과 같은 모양이다. 미리보기와 내려받는 PDF 는
 * 렌더러가 다르므로 <b>이 값을 고치면 서버 틀도 같이 고친다.</b>
 */

/** 머리 표 한 칸에 올 값. */
export type MetaField = 'site' | 'date' | 'kind' | 'time' | 'target' | 'headcount' | 'place' | 'instructor' | 'inspector';

/**
 * 본문 칸. lines 는 안전보건일지의 위험요인 · 안전조치 표, patrolNotes · activities · facilities 는 안전보건의
 * 나머지 표, checks 는 순회점검 점검표다.
 */
export type FormSection = 'content' | 'lines' | 'photos' | 'checks' | 'patrolNotes' | 'activities' | 'facilities';

export type MetaCell = { field: MetaField; label: string };

export type JournalForm = {
  heading: string;
  /** 머리 표 — 한 줄에 두 칸(이름 · 값 · 이름 · 값). 한 칸짜리 줄은 값이 줄 끝까지 간다. */
  meta: Array<[MetaCell, MetaCell] | [MetaCell]>;
  /** 본문의 순서와 소제목. 사진은 비어 있으면 빠진다(서버와 같다). */
  sections: Array<{ key: FormSection; label: string }>;
  /** 위험요인 표의 칸 — 폭은 %, null 이면 남는 폭. 다른 일지는 비어 있다. */
  columns: Array<{ label: string; width: number | null }>;
};

const SITE: MetaCell = { field: 'site', label: '현장' };

export const DEFAULT_FORMS: Record<JournalType, JournalForm> = {
  EDUCATION: {
    heading: '안전교육일지',
    meta: [
      [SITE, { field: 'date', label: '교육일시' }],
      [{ field: 'kind', label: '교육구분' }],
      [{ field: 'time', label: '교육시간' }],
      [
        { field: 'target', label: '대상' },
        { field: 'headcount', label: '인원' },
      ],
      [
        { field: 'place', label: '장소' },
        { field: 'instructor', label: '강사' },
      ],
    ],
    sections: [
      { key: 'content', label: '교육내용' },
      { key: 'photos', label: '교육 사진' },
    ],
    columns: [],
  },
  HEALTH: {
    heading: '안전보건일지',
    meta: [[SITE, { field: 'date', label: '일자' }]],
    sections: [
      { key: 'patrolNotes', label: 'Patrol 점검 현황' },
      { key: 'activities', label: '안전활동 및 회의' },
      { key: 'facilities', label: '안전시설물 설치 현황' },
      { key: 'lines', label: '작업별 위험요인 · 안전조치' },
      { key: 'photos', label: '사진' },
    ],
    columns: [
      { label: 'No', width: 5 },
      { label: '작업내용', width: 20 },
      { label: '위치', width: 12 },
      { label: '위험요인', width: 24 },
      { label: '안전조치', width: 24 },
      { label: '이행', width: 7 },
      { label: '비고', width: null },
    ],
  },
  // 순회점검일지는 종이 양식 한 장이라 JournalPaper 의 PatrolSheet 가 직접 그린다(DOC-122 §11). 아래는 차례만 적어 둔다.
  PATROL: {
    heading: '순회점검일지',
    meta: [[{ field: 'site', label: '현장명' }, { field: 'date', label: '일시' }]],
    sections: [
      { key: 'checks', label: '현장 순회 점검 내용' },
      { key: 'content', label: '특이사항 및 조치사항' },
      { key: 'photos', label: '사진' },
    ],
    columns: [],
  },
  JOINT: {
    heading: '합동점검일지',
    meta: [[{ field: 'site', label: '현장명' }, { field: 'date', label: '일시' }]],
    sections: [
      { key: 'checks', label: '합동 점검 내용' },
      { key: 'content', label: '특이사항 및 조치사항' },
      { key: 'photos', label: '사진' },
    ],
    columns: [],
  },
  // 위험성평가 · TBM 일지(V572)는 종이가 아니라 표 한 장이다 — RiskSheet · TbmSheet 가 그린다. 아래는 이름만.
  RISK: { heading: '위험성평가', meta: [], sections: [], columns: [] },
  TBM: { heading: 'TBM 일지', meta: [], sections: [], columns: [] },
  // 양식 문서(V574)도 종이가 아니다 — DocSheet 가 칸 정의대로 그린다.
  ...(Object.fromEntries(
    Object.entries(DOC_TYPES).map(([code, one]) => [code, { heading: one.label, meta: [], sections: [], columns: [] }]),
  ) as unknown as Record<DocType, JournalForm>),
};
